import { FormControl, FormLabel } from "@chakra-ui/form-control";
import { Stack } from "@chakra-ui/layout";
import { Button, Flex, Modal, Radio, RadioGroup, Select, TextInput, useTranslation } from "@familyzone/component-library";
import { Item } from "@familyzone/component-library/dist/components/Select";
import React, { useState } from "react";

export type GuestLifetime = "1hour" | "1day" | "2day" | "1week" | "1month" | "3month" | "6month";
const DEFAULT_LIFETIME: GuestLifetime = "1day";
const LIFETIME_ITEMS: Item[] = [
  { value: "1hour", text: "1 Hour" },
  { value: "1day", text: "1 Day" },
  { value: "2day", text: "2 Days" },
  { value: "1week", text: "1 Week" },
  { value: "1month", text: "1 Month" },
  { value: "3month", text: "3 Months" },
  { value: "6month", text: "6 Months" },
];
const isGuestLifetime = (value: string): value is GuestLifetime => {
  return LIFETIME_ITEMS.some((item) => item.value === value);
};

const NAME_EMPTY_ERROR = "Names must not be empty.";
const NAME_ALPHANUMERIC_ERROR = "Names must be alphanumeric.";
const isNonEmptyName = (value: string): boolean => value.trim() !== "";
const isAlphanumericName = (value: string): boolean => /^[a-zA-Z0-9]+$/.test(value);
const isValidName = (value: string): boolean => isNonEmptyName(value) && isAlphanumericName(value);

type GuestExpiry = "first_login" | "account_creation";
const DEFAULT_EXPIRY: GuestExpiry = "first_login";

export type AddGuestPayload = {
  firstname: string;
  lastname: string;
  expire_after_first_login: boolean;
  expire_after_account_creation: boolean;
  lifetime: GuestLifetime;
};

type AddGuestProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: AddGuestPayload) => void;
};

const AddGuest: React.FC<AddGuestProps> = ({ open, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState("Guest");
  const [lastName, setLastName] = useState("Guest");
  const [expiry, setExpiry] = useState<GuestExpiry>(DEFAULT_EXPIRY);
  const [lifetime, setLifetime] = useState<GuestLifetime>(DEFAULT_LIFETIME);
  const [firstNameError, setFirstNameError] = useState<string | undefined>();
  const [lastNameError, setLastNameError] = useState<string | undefined>();

  const validateName = (value: string): string | undefined => {
    if (!isNonEmptyName(value)) {
      return t(NAME_EMPTY_ERROR);
    }
    if (!isAlphanumericName(value)) {
      return t(NAME_ALPHANUMERIC_ERROR);
    }
    return undefined;
  };

  const resetForm = () => {
    setFirstName("Guest");
    setLastName("Guest");
    setExpiry(DEFAULT_EXPIRY);
    setLifetime(DEFAULT_LIFETIME);
    setFirstNameError(undefined);
    setLastNameError(undefined);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = () => {
    if (!isValidName(firstName) || !isValidName(lastName)) {
      return;
    }

    onSubmit({
      firstname: firstName,
      lastname: lastName,
      expire_after_first_login: expiry === "first_login",
      expire_after_account_creation: expiry === "account_creation",
      lifetime,
    });
    handleClose();
  };

  const selectedLifetime = LIFETIME_ITEMS.find((item) => item.value === lifetime);

  return (
    <Modal isOpen={open} onClose={handleClose} headerText={t("Add a Guest")} size="md" contentProps={{ style: { overflow: "visible" } }}>
      <Stack spacing="sp12" width="100%" my="sp12">
        <FormControl>
          <FormLabel>{t("First Name")}</FormLabel>
          <TextInput
            name="firstname"
            aria-label={t("First Name")}
            value={firstName}
            onChange={(e) => {
              const value = e.target.value;
              setFirstName(value);
              setFirstNameError(validateName(value));
            }}
            errorMessage={firstNameError}
          />
        </FormControl>
        <FormControl>
          <FormLabel>{t("Last Name")}</FormLabel>
          <TextInput
            name="lastname"
            aria-label={t("Last Name")}
            value={lastName}
            onChange={(e) => {
              const value = e.target.value;
              setLastName(value);
              setLastNameError(validateName(value));
            }}
            errorMessage={lastNameError}
          />
        </FormControl>
        <FormControl>
          <RadioGroup
            value={expiry}
            onChange={(value) => {
              if (value === "first_login" || value === "account_creation") {
                setExpiry(value);
              }
            }}
          >
            <Radio value="first_login" pr="sp12">
              {t("Expire after First Login")}
            </Radio>
            <Radio value="account_creation">{t("Expire after Account Creation")}</Radio>
          </RadioGroup>
        </FormControl>
        <FormControl>
          <FormLabel>{t("Lifetime")}</FormLabel>
          <Select
            items={LIFETIME_ITEMS}
            placeholder={t("Select a lifetime")}
            selectedItem={selectedLifetime}
            onChange={(item) => {
              if (item && isGuestLifetime(item.value)) {
                setLifetime(item.value);
              }
            }}
          />
        </FormControl>
      </Stack>
      <Flex>
        <Button
          variant="primary"
          onClick={handleSubmit}
          data-testid="add-guest-button"
          disabled={!isValidName(firstName) || !isValidName(lastName)}
        >
          {t("Add")}
        </Button>
      </Flex>
    </Modal>
  );
};

export default AddGuest;
